import React, { useEffect, useMemo, useState } from "react";
import { api, imgUrl } from "../lib/api";
import { X } from "lucide-react";

const FILTERS = ["All", "Mixed Media", "Acrylic", "Collage", "Oil"];

const Portfolio = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);

  useEffect(() => {
    api
      .get("/artworks")
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, []);

  // Track artwork view when lightbox opens
  useEffect(() => {
    if (!active) return;
    api
      .post("/analytics/track", {
        path: `/portfolio/${active.id}`,
        referrer: document.referrer || null,
        artwork_id: active.id,
      })
      .catch(() => {});
  }, [active]);

  const filtered = useMemo(
    () =>
      filter === "All"
        ? items
        : items.filter((a) => a.medium.toLowerCase() === filter.toLowerCase()),
    [items, filter]
  );

  // bento aspect classes - cycle through pleasing ratios
  const aspectFor = (i) => {
    const cycle = [
      "aspect-[4/5]",
      "aspect-square",
      "aspect-[3/4]",
      "aspect-[5/4]",
      "aspect-[4/5]",
      "aspect-[3/4]",
    ];
    return cycle[i % cycle.length];
  };

  return (
    <div className="px-6 md:px-12 lg:px-20 py-16 md:py-24" data-testid="page-portfolio">
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-6">
        <div>
          <div className="label-eyebrow mb-3">Portfolio</div>
          <h1 className="font-serif-display text-5xl md:text-6xl text-[#1A1F1B]">
            A gallery of works.
          </h1>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="portfolio-filters">
          {FILTERS.map((f) => (
            <button
              type="button"
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border transition-colors ${
                filter === f
                  ? "bg-[#3B4A3F] text-[#F7F6F2] border-[#3B4A3F]"
                  : "border-[#D8D3CC] text-[#4E5C50] hover:border-[#3B4A3F]"
              }`}
              data-testid={`portfolio-filter-${f.toLowerCase().replace(" ", "-")}`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="py-32 text-center text-[#849383]" data-testid="portfolio-loading">
          Loading gallery…
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-32 text-center text-[#849383]" data-testid="portfolio-empty">
          No works in this category yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-testid="portfolio-grid">
          {filtered.map((a, i) => (
            <button
              type="button"
              key={a.id}
              onClick={() => setActive(a)}
              className="art-card text-left group"
              data-testid={`portfolio-item-${a.id}`}
            >
              <div className="overflow-hidden bg-[#EFECE5]">
                <img
                  src={imgUrl(a.image_url)}
                  alt={a.title}
                  className={`w-full object-contain ${aspectFor(i)}`}
                />
              </div>
              <div className="mt-4">
                <div className="font-serif-display text-xl text-[#1A1F1B]">
                  {a.title}
                </div>
                <div className="label-eyebrow mt-1">
                  {a.medium} · {a.year}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {active && (
        <div
          className="fixed inset-0 z-50 bg-[#1A1F1B]/80 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={() => setActive(null)}
          data-testid="portfolio-lightbox"
        >
          <button
            type="button"
            onClick={() => setActive(null)}
            className="absolute top-6 right-6 p-2 text-[#F7F6F2] hover:bg-white/10"
            aria-label="Close"
            data-testid="portfolio-lightbox-close"
          >
            <X size={24} />
          </button>
          <div
            className="max-w-5xl w-full grid md:grid-cols-5 gap-8 bg-[#F7F6F2] p-6 md:p-10"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={imgUrl(active.image_url)}
              alt={active.title}
              className="md:col-span-3 w-full h-full max-h-[78vh] object-contain"
            />
            <div className="md:col-span-2 flex flex-col">
              <div className="label-eyebrow">{active.medium}</div>
              <h3 className="font-serif-display text-3xl md:text-4xl mt-2 text-[#1A1F1B]">
                {active.title}
              </h3>
              <div className="text-sm text-[#849383] mt-1">
                {active.year}
                {active.dimensions && <> · {active.dimensions}</>}
              </div>
              {active.description && (
                <p className="mt-6 text-[#4E5C50] leading-relaxed">
                  {active.description}
                </p>
              )}
              <div className="mt-auto pt-8 text-xs text-[#849383]">
                For inquiries about availability, please use the contact page.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Portfolio;
